import { Component, OnInit, inject, model, signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { faCirclePlus } from '@fortawesome/pro-regular-svg-icons';
import { take } from 'rxjs';
import { RecurrenceOptions } from '../../../../../core/constants-repository/recurrence.constants';
import { UserOperation } from '../../../../../core/enums/user-operation';
import { FileNameModel } from '../../../../../core/models/file/file-name.model';
import { FileModel } from '../../../../../core/models/file/file.model';
import {
  OperatingCostsModel,
  ProjectExpenseModel,
} from '../../../../../core/models/finance/expense.model';
import { ShiveDialogService } from '../../../../../core/services/controls/shive-dialog.service';
import { FileService } from '../../../../../core/services/file.service';
import { RECURRENCE_LABELS } from '../../../../../core/text-db/recurrence.text';
import { DropdownOption } from '../../../../../shared/data-types/dropdown-option';
import { FileEntityKey, FileEntityKind } from '../../../../../shared/data-types/file-types';
import { FormControlNameRecord } from '../../../../../shared/data-types/form-types';
import { HttpParamObject } from '../../../../../shared/data-types/http-types';
import { recordToDropdownOptions } from '../../../../../shared/functions/dropdown-functions';

@Component({
  selector: 'app-operating-costs-dialog',
  templateUrl: './operating-costs-dialog.component.html',
  standalone: false,
})
export class OperatingCostsDialogComponent implements OnInit {
  selectedTags = signal<any[]>([]);
  recurrenceOptions = signal<DropdownOption[]>([]);
  operatingCosts = model.required<OperatingCostsModel>();
  public operatingCostsForm: FormGroup;

  // Constants
  public readonly faCirclePlus = faCirclePlus;
  public formControlNames: FormControlNameRecord = {
    Identifier: 'identifier',
    Date: 'date',
    Amount: 'amount',
    Supplier: 'supplier',
    Due: 'due',
    Recurrence: 'recurrence',
    RecurrenceEnd: 'recurrenceEnd',
    DeductFromBudget: 'deductFromBudget',
    IsPaid: 'isPaid',
    IsBillable: 'isBillable',
    Attachment: 'attachment',
  };

  // DI
  private readonly shiveDialogService = inject(ShiveDialogService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly fileService = inject(FileService);

  ngOnInit(): void {
    this.buildForm();
  }

  public close(): void {
    this.shiveDialogService.close(UserOperation.Cancel);
  }

  public submit(): void {
    const form = this.operatingCostsForm;
    const values = form.getRawValue();

    const recurrence = values[this.formControlNames.Recurrence] as DropdownOption | null;

    // Handle files
    let fileModel = null;
    const files = values[this.formControlNames.Attachment] as File[];
    if (files?.length > 0) {
      fileModel = new FileModel();
      fileModel.file = files[0];
    }

    const projectExpenseModel: ProjectExpenseModel = Object.assign(
      new ProjectExpenseModel(),
      this.operatingCosts(),
      {
        name: values[this.formControlNames.Identifier],
        invoiceDate: values[this.formControlNames.Date],
        amount: values[this.formControlNames.Amount],
        supplier: values[this.formControlNames.Supplier],
        due: values[this.formControlNames.Due],

        recurrence: recurrence?.value as RecurrenceOptions,
        recurrenceEnd: values[this.formControlNames.RecurrenceEnd],

        isPaid: values[this.formControlNames.IsPaid],

        tagIds: this.selectedTags().map((tag) => tag.id),
        attachment: fileModel,
      },
    );

    this.shiveDialogService.close(projectExpenseModel);
  }

  public downloadFile(): void {
    const fileId = this.operatingCosts().attachment.id;
    const params: HttpParamObject = {
      kind: FileEntityKind.Expense,
      [FileEntityKey.FileId]: fileId,
    };
    this.fileService.fetchFile(params, true);
  }

  private buildForm() {
    recordToDropdownOptions(RECURRENCE_LABELS)
      .pipe(take(1))
      .subscribe((recurrenceOptions) => {
        this.recurrenceOptions.set(recurrenceOptions);
        let selectedOption = recurrenceOptions.find(
          (option) => option.value === this.operatingCosts().recurrence,
        );

        if (!selectedOption) {
          selectedOption = this.recurrenceOptions()[0];
        }

        // Set file
        const fileName = (this.operatingCosts().attachment as FileNameModel)?.fileName;
        let fileValue = null;
        if (fileName) {
          // Create a mock File object
          const mockFile = new File([], fileName, { type: 'application/octet-stream' });
          fileValue = [mockFile]; // kendo-fileselect expects an array
        }

        // Set tags
        this.selectedTags.set(this.operatingCosts().tags);

        this.operatingCostsForm = this.formBuilder.group({
          [this.formControlNames.Identifier]: [this.operatingCosts().name, Validators.required],
          [this.formControlNames.Date]: [this.operatingCosts().invoiceDate, Validators.required],
          [this.formControlNames.Supplier]: [this.operatingCosts().supplier, Validators.required],
          [this.formControlNames.Amount]: [+this.operatingCosts().amount, Validators.required],
          [this.formControlNames.Due]: [this.operatingCosts().due, Validators.required],
          [this.formControlNames.Recurrence]: [selectedOption, Validators.required],
          [this.formControlNames.RecurrenceEnd]: [
            this.operatingCosts().recurrenceEnd,
            Validators.required,
          ],
          [this.formControlNames.IsPaid]: [this.operatingCosts().isPaid, Validators.required],
          [this.formControlNames.Attachment]: [fileValue, Validators.required],
        });
      });
  }
}
